import React from 'react'
import TweenFunctions from 'tween-functions'
import PropTypes from 'prop-types'

class ScrollUpButton extends React.Component {
	constructor(props) {
		super(props)
		this.state = { ToggleScrollUp: '' }
		this.Animation = {
			StartPosition: 0,
			CurrentAnimationTime: 0,
			StartTime: null,
			AnimationFrame: null,
		}
		this.HandleScroll = this.HandleScroll.bind(this)
		this.StopScrollingFrame = this.StopScrollingFrame.bind(this)
		this.ScrollingFrame = this.ScrollingFrame.bind(this)
		this.HandleClick = this.HandleClick.bind(this)
	}

	componentDidMount() {
		var supportsPassive = false
		try {
			var opts = Object.defineProperty({}, 'passive', {
				get: function () {
					supportsPassive = true
					return true
				},
			})
			window.addEventListener('testPassive', null, opts)
			window.removeEventListener('testPassive', null, opts)
		} catch (e) {
			console.error(e)
		}

		this.HandleScroll()
		window.addEventListener('scroll', this.HandleScroll)
		window.addEventListener(
			'wheel',
			this.StopScrollingFrame,
			supportsPassive ? { passive: true } : false
		)
		window.addEventListener(
			'touchstart',
			this.StopScrollingFrame,
			supportsPassive ? { passive: true } : false
		)
	}

	componentWillUnmount() {
		window.removeEventListener('scroll', this.HandleScroll)
		window.removeEventListener('wheel', this.StopScrollingFrame, false)
		window.removeEventListener('touchstart', this.StopScrollingFrame, false)
	}

	HandleScroll() {
		const { ShowAtPosition, TransitionClassName } = this.props
		if (window.pageYOffset > ShowAtPosition) {
			this.setState({ ToggleScrollUp: TransitionClassName })
		} else {
			this.setState({ ToggleScrollUp: '' })
		}
	}

	HandleClick() {
		this.StopScrollingFrame()
		this.Animation.StartPosition = window.pageYOffset
		this.Animation.CurrentAnimationTime = 0
		this.Animation.StartTime = null
		this.Animation.AnimationFrame = window.requestAnimationFrame(this.ScrollingFrame)
	}

	ScrollingFrame() {
		const { StopPosition, EasingType, AnimationDuration } = this.props
		const timestamp = Math.floor(Date.now())
		// If StartTime has not been assigned a value, assign it the start timestamp.
		if (!this.Animation.StartTime) {
			this.Animation.StartTime = timestamp
		}

		// set CurrentAnimationTime every iteration of ScrollingFrame()
		this.Animation.CurrentAnimationTime = timestamp - this.Animation.StartTime
		// if we hit the StopPosition, StopScrollingFrame()
		if (window.pageYOffset <= StopPosition) {
			this.StopScrollingFrame()
		} else {
			// Otherwise continue ScrollingFrame to the StopPosition.
			// Does not support horizontal ScrollingFrame.
			// Let TweenFunctions handle the math to give us a new position based on AnimationDuration and EasingType type
			let YPos = TweenFunctions[EasingType](
				this.Animation.CurrentAnimationTime,
				this.Animation.StartPosition,
				StopPosition,
				AnimationDuration
			)
			if (YPos <= StopPosition) {
				YPos = StopPosition
			}
			window.scrollTo(0, YPos)
			// Request another frame to be painted
			this.Animation.AnimationFrame = window.requestAnimationFrame(this.ScrollingFrame)
		}
	}

	StopScrollingFrame() {
		window.cancelAnimationFrame(this.Animation.AnimationFrame)
	}

	render() {
		const { children, ToggledClasses, ContainerClassName, classes, HiddenClasses, cartItemCount } =
			this.props
		const { ToggleScrollUp } = this.state
		return (
			<aside
				role="button"
				aria-label="Scroll to top of page"
				// tabIndex={ToggleScrollUp ? 0 : -1}
				data-testid="react-scroll-up-button"
				className={`${ContainerClassName} ${ToggleScrollUp} ${classes} ${
					ToggleScrollUp !== '' ? ToggledClasses : HiddenClasses
				} ${cartItemCount.length > 0 ? 'proceedToTop' : ''}`}
				onClick={this.HandleClick}
				onKeyPress={this.HandleClick}
			>
				{children}
			</aside>
		)
	}
}
export default ScrollUpButton

ScrollUpButton.defaultProps = {
	ContainerClassName: 'ScrollUpButton__Container',
	StopPosition: 0,
	ShowAtPosition: 150,
	EasingType: 'easeOutCubic',
	AnimationDuration: 500,
	TransitionClassName: 'ScrollUpButton__Toggled',
	style: {},
	ToggledStyle: {},
	children: null,
}

function LessThanShowAtPosition(props, propName, componentName) {
	const { ShowAtPosition } = props
	if (props[propName]) {
		// eslint-disable-line
		const value = props[propName]
		if (typeof value === 'number') {
			if (value >= ShowAtPosition) {
				// Validate the incoming prop value againt the ShowAtPosition prop
				return new Error(
					`${propName} (${value}) in ${componentName} must be less then prop: ShowAtPosition (${ShowAtPosition})`
				)
			}
			return null
		}
		return new Error(`${propName} in ${componentName} must be a number.`)
	}
	return null
}

ScrollUpButton.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	StopPosition: LessThanShowAtPosition,
	ShowAtPosition: PropTypes.number, // show button under this position,
	EasingType: PropTypes.oneOf([
		'linear',
		'easeInQuad',
		'easeOutQuad',
		'easeInOutQuad',
		'easeInCubic',
		'easeOutCubic',
		'easeInOutCubic',
		'easeInQuart',
		'easeOutQuart',
		'easeInOutQuart',
		'easeInQuint',
		'easeOutQuint',
		'easeInOutQuint',
		'easeInSine',
		'easeOutSine',
		'easeInOutSine',
		'easeInExpo',
		'easeOutExpo',
		'easeInOutExpo',
		'easeInCirc',
		'easeOutCirc',
		'easeInOutCirc',
		'easeInElastic',
		'easeOutElastic',
		'easeInOutElastic',
		'easeInBack',
		'easeOutBack',
		'easeInOutBack',
		'easeInBounce',
		'easeOutBounce',
		'easeInOutBounce',
	]),
	AnimationDuration: PropTypes.number, // seconds
	style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	ToggledStyle: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	ContainerClassName: PropTypes.string,
	TransitionClassName: PropTypes.string,
}
